import { capitalize } from 'lodash-es';
import { DefaultSeoProps } from 'next-seo';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useCanonicalUrl } from '@pflegia/ui/hooks/use-canonical-url';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { getAppNameFromDomain } from '@pflegia/util/pflegia/get-app-name-from-domain';

import { LOCALES } from '@shared/constants/locale';

import { getBaseUrlByHostName, getDomainWithPrefixByHostName } from '@utils/domain';

import { useLogoUrl } from './use-logo-url';

export const useSeoConfig = () => {
  const { locale = LOCALES[0] } = useRouter();
  const { sourceDomainName, sourceDomain } = useFlavorFromRoute();
  const logoUrl = useLogoUrl();
  const getCanonicalUrl = useCanonicalUrl();

  const seoConfig = useMemo(() => {
    const openGraphUrl = getCanonicalUrl({
      defaultDomain: sourceDomain,
      flavorDomain: sourceDomain,
      url: getBaseUrlByHostName(sourceDomain),
    });

    const config: DefaultSeoProps = {
      languageAlternates: [
        {
          href: openGraphUrl,
          hrefLang: LOCALES[0],
        },
      ],
      openGraph: {
        defaultImageHeight: 1200,
        defaultImageWidth: 630,
        description: `${capitalize(getDomainWithPrefixByHostName(sourceDomain))} - Pflege-Jobs, die zu Dir passen`,
        images: [{ url: logoUrl }],
        locale: getOpenGraphLocale(locale),
        siteName: sourceDomainName,
        title: sourceDomainName,
        type: 'basic',
        url: openGraphUrl,
      },
      titleTemplate: `%s | ${sourceDomainName}`,
      twitter: {
        handle: `@${getAppNameFromDomain(sourceDomain).toLowerCase()}`,
        site: `@${getAppNameFromDomain(sourceDomain).toLowerCase()}`,
        cardType: 'summary_large_image',
      },
    };
    return config;
  }, [getCanonicalUrl, locale, logoUrl, sourceDomainName, sourceDomain]);

  return seoConfig;
};

const getOpenGraphLocale = (locale: string) => {
  if (locale.includes('_')) return locale;
  if (locale.includes('-')) return locale.replace('-', '_');
  return 'de_DE';
};
